import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Menu, LogOut, Sun, Moon, Settings, Activity, MessageCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import logoLight from '../../assets/logo.png';
import logoDark from '../../assets/logo-dark.png';

function Header({ mobileSidebarOpen, setMobileSidebarOpen }) {
  const { currentUser, logout } = useAuth();
  const { darkMode, toggleDarkMode } = useTheme();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  
  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    }
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  return (
    <header 
      className=""
      style={{ backgroundColor: 'rgb(var(--app-background))' }}
    >
      <div className="flex justify-between items-center px-6 py-3">
        <div className="flex items-center">
          <button 
            className="md:hidden mr-4 text-slate-500 hover:text-slate-700 p-2 rounded-lg hover:bg-slate-100 transition-all duration-200
                     dark:text-slate-400 dark:hover:text-white dark:hover:bg-slate-800 transform hover:scale-105 hover:shadow-md" 
            onClick={() => setMobileSidebarOpen(!mobileSidebarOpen)}
          >
            <Menu size={18} />
          </button>
          <div className="md:hidden flex items-center space-x-2">
            <img src={darkMode ? logoDark : logoLight} alt="Plexe Logo" className="h-7 w-7 object-contain" />
            <span className="text-lg font-semibold text-slate-900 dark:text-white">Plexe</span>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          {/* Early Access Badge */}
          <span className="inline-flex items-center px-2 py-1 text-xs font-semibold uppercase tracking-wider text-blue-600 bg-blue-50 border border-blue-200 rounded dark:text-blue-400 dark:bg-blue-900/20 dark:border-blue-800">
            Early Access
          </span>

          {/* Documentation link */}
          <Link
            to="https://github.com/plexe-ai"
            target="_blank"
            className="text-sm text-slate-600 hover:text-slate-900 hover:bg-slate-100 p-2 rounded-lg transition-all duration-200 transform hover:scale-105 hover:shadow-md
                     dark:text-slate-400 dark:hover:text-white dark:hover:bg-slate-800"
          >
            Docs
          </Link>

          {/* Support link */}
          <a
            href="https://discord.gg/SefZDepGMv"
            target="_blank"
            rel="noopener noreferrer"
            className="p-2 rounded-lg text-slate-600 hover:text-slate-900 hover:bg-slate-100 transition-all duration-200
                     dark:text-slate-400 dark:hover:text-white dark:hover:bg-slate-800 transform hover:scale-105 hover:shadow-md"
            title="Support"
          >
            <MessageCircle size={18} />
          </a>

          {/* Dark mode toggle */}
          <button 
            onClick={toggleDarkMode}
            className="p-2 rounded-lg text-slate-600 hover:text-slate-900 hover:bg-slate-100 transition-all duration-200
                     dark:text-slate-400 dark:hover:text-white dark:hover:bg-slate-800 transform hover:scale-105 hover:shadow-md"
          >
            {darkMode ? <Sun size={18} /> : <Moon size={18} />}
          </button>
          
          {/* Profile dropdown */}
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="h-8 w-8 rounded-full overflow-hidden border border-slate-200 dark:border-slate-700 cursor-pointer hover:border-primary-300 transition-all duration-200 transform hover:scale-110 hover:shadow-lg"
            >
              {currentUser?.picture ? (
                <img src={currentUser.picture} alt="Profile" className="h-full w-full object-cover" />
              ) : (
                <div className="h-full w-full bg-primary-600 flex items-center justify-center text-white font-bold">
                  {currentUser?.email
                    ? currentUser.email.charAt(0).toUpperCase()
                    : currentUser?.name
                      ? currentUser.name.charAt(0).toUpperCase()
                      : 'U'}
                </div>
              )}
            </button>
            
            {dropdownOpen && (
              <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-medium py-1 z-50 border border-slate-200
                            dark:bg-slate-800 dark:border-slate-700">
                <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700">
                  <p className="text-sm font-medium text-slate-900 dark:text-white">
                    {currentUser?.name || currentUser?.email || 'User'}
                  </p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    {currentUser?.email}
                  </p>
                </div>
                <Link 
                  to="/usage" 
                  className="flex items-center px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors
                           dark:text-slate-200 dark:hover:bg-slate-700"
                  onClick={() => setDropdownOpen(false)}
                >
                  <Activity size={18} className="mr-3" />
                  Usage
                </Link>
                <Link 
                  to="/settings" 
                  className="flex items-center px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors
                           dark:text-slate-200 dark:hover:bg-slate-700"
                  onClick={() => setDropdownOpen(false)}
                >
                  <Settings size={18} className="mr-3" />
                  Settings
                </Link>
                <button
                  onClick={() => {
                    setDropdownOpen(false);
                    logout();
                  }}
                  className="w-full flex items-center px-4 py-2 text-sm text-slate-700 hover:bg-red-50 hover:text-red-600 transition-all duration-200
                           dark:text-slate-200 dark:hover:bg-red-900/20 dark:hover:text-red-400 transform hover:scale-[1.02]"
                >
                  <LogOut size={18} className="mr-3" />
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}

export default Header;